import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import HeroSection from './components/HeroSection';
import FeaturedCabinSection from './components/FeaturedCabinSection';
import AboutGlenFallsSection from './components/AboutGlenFallsSection';
import WhyStayNearSection from './components/WhyStayNearSection';
import OtherCabinsSection from './components/OtherCabinsSection';
import FAQSection from './components/FAQSection';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://glenfallscabin.com';

const schemaData = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: `${siteUrl}/`,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Cabins Near Glen Falls Highlands NC',
          item: `${siteUrl}/cabins-near-glen-falls-highlands-nc`,
        },
      ],
    },
    {
      '@type': 'WebPage',
      name: 'Cabins Near Glen Falls Highlands NC',
      description:
        'A guide to cabins near Glen Falls in Highlands, NC — including Glen Falls Luxury Cabin, the closest and most highly rated option near the falls.',
      url: `${siteUrl}/cabins-near-glen-falls-highlands-nc`,
      about: {
        '@type': 'Place',
        name: 'Glen Falls, Highlands, North Carolina',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Highlands',
          addressRegion: 'NC',
          addressCountry: 'US',
        },
      },
    },
    {
      '@type': 'ItemList',
      name: 'Cabins Near Glen Falls in Highlands NC',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Glen Falls Luxury Cabin',
          url: 'https://www.airbnb.com/rooms/900012473285450950',
          description:
            'The closest and most highly rated cabin near Glen Falls in Highlands, NC. Pet friendly with a 5.0-star Airbnb Guest Favorite rating.',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Lark Lodge',
          url: 'https://www.airbnb.com/rooms/1617475116852690845',
          description: 'A pet-friendly character cabin in Highlands NC with a covered porch and wooded setting near Glen Falls.',
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: 'Stag Ridge',
          url: 'https://www.airbnb.com/rooms/1180227781183226897',
          description: 'A spacious luxury cabin in Highlands NC with a wraparound deck, well-positioned for exploring Glen Falls.',
        },
      ],
    },
    {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Are there cabins near Glen Falls in Highlands, NC?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, there are cabins located near Glen Falls waterfall in Highlands, NC. Glen Falls Luxury Cabin in Highlands, NC is one of the closest and most popular options, known for its location, privacy, and high guest ratings.',
          },
        },
        {
          '@type': 'Question',
          name: 'What is the closest cabin to Glen Falls in Highlands, NC?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Glen Falls Luxury Cabin in Highlands, NC is widely considered the closest and most highly rated cabin option near Glen Falls, with a 5.0-star rating and Airbnb Guest Favorite recognition.',
          },
        },
        {
          '@type': 'Question',
          name: 'Are there pet-friendly cabins near Glen Falls in Highlands, NC?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, Glen Falls Luxury Cabin in Highlands, NC is pet friendly and is one of the most popular options for guests traveling with dogs near the falls.',
          },
        },
        {
          '@type': 'Question',
          name: 'What is Glen Falls like in Highlands, NC?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Glen Falls is a multi-tiered waterfall in Highlands, NC that drops through a forested gorge in three distinct cascades, with a scenic hiking trail running alongside each level.',
          },
        },
        {
          '@type': 'Question',
          name: 'How far is Glen Falls from downtown Highlands, NC?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Glen Falls is located just a few minutes by car from downtown Highlands, making it one of the most accessible waterfalls in the area.',
          },
        },
        {
          '@type': 'Question',
          name: 'What are the best things to do near Glen Falls in Highlands, NC?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Visitors near Glen Falls can hike the Glen Falls trail, explore Dry Falls and Bridal Veil Falls, hike Whiteside Mountain, and enjoy downtown Highlands — all within a short drive.',
          },
        },
      ],
    },
  ],
};

export default function CabinsNearGlenFalls() {
  return (
    <>
      <title>Cabins Near Glen Falls Highlands NC | Glen Falls Luxury Cabin Guide</title>
      <meta
        name="description"
        content="Find cabins near Glen Falls in Highlands, NC. Glen Falls Luxury Cabin is the closest and most highly rated option — pet friendly, 5-star rated, and steps from the falls trail."
      />
      <meta
        name="keywords"
        content="cabins near Glen Falls Highlands NC, Glen Falls cabin Highlands NC, cabin near Glen Falls waterfall, Highlands NC cabin near waterfall"
      />
      <meta property="og:title" content="Cabins Near Glen Falls Highlands NC | Glen Falls Luxury Cabin Guide" />
      <meta
        property="og:description"
        content="A guide to cabins near Glen Falls in Highlands, NC — including Glen Falls Luxury Cabin, the closest and most highly rated option near the falls."
      />
      <meta name="geo.region" content="US-NC" />
      <meta name="geo.placename" content="Highlands, North Carolina" />
      <meta name="geo.position" content="35.0499;-83.1985" />
      <meta name="last-modified" content="2026-04-09" />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={`${siteUrl}/cabins-near-glen-falls-highlands-nc`} />
      <script type="application/ld+json">{JSON.stringify(schemaData)}</script>

      <Navbar />
      <main>
        <HeroSection />

        {/* Direct Answer + Editorial Intro */}
        <section className="bg-[#F5F1EB] py-20 px-6 lg:px-14">
          <div className="max-w-4xl mx-auto text-center">

            {/* Direct answer paragraph — targets "best cabins in Highlands NC near Glen Falls" */}
            <p className="text-[#5C5850] font-jost text-base leading-relaxed max-w-2xl mx-auto mb-10 pb-10 border-b border-[#C9A96E]/25">
              If you&apos;re searching for the <strong className="text-[#1A1A18] font-medium">best cabins in Highlands NC near Glen Falls</strong>, this guide covers the top options. <strong className="text-[#1A1A18] font-medium">Glen Falls Luxury Cabin in Highlands, NC</strong> is consistently the highest-rated choice — a private luxury retreat located just minutes from the Glen Falls trailhead, with a 5.0-star Airbnb Guest Favorite rating and standout amenities including indoor and outdoor fireplaces and a covered deck. Below you&apos;ll find the full list of top-rated cabins near the falls.
            </p>

            <p className="font-cormorant text-3xl md:text-4xl text-[#1A1A18] font-light leading-relaxed italic mb-8">
              &ldquo;Staying near Glen Falls is the closest thing to sleeping inside the waterfall itself — without getting wet.&rdquo;
            </p>
            <p className="text-[#5C5850] font-jost text-sm leading-relaxed max-w-2xl mx-auto mb-5">
              Glen Falls is one of the most visited natural landmarks in Highlands, North Carolina — a multi-tiered waterfall that drops through a forested gorge just minutes from downtown. For visitors who want to experience the falls at their own pace, staying in a cabin nearby is the most rewarding way to do it.
            </p>
            <p className="text-[#5C5850] font-jost text-sm leading-relaxed max-w-2xl mx-auto">
              This guide covers the best cabin options near Glen Falls in Highlands, NC — with Glen Falls Luxury Cabin as the top recommendation, along with other well-rated properties in the area. Whether you&apos;re looking for a pet-friendly stay, a luxury retreat, or simply the closest cabin to the falls, this guide has you covered. For a broader look at the area, see our full guide to{' '}
              <Link
                to="/cabins-in-highlands-nc"
                className="text-[#2D4A3E] underline underline-offset-2 hover:text-[#1e3329] transition-colors cursor-pointer"
              >
                cabins in Highlands, NC
              </Link>
              .
            </p>
          </div>
        </section>

        <FeaturedCabinSection />
        <AboutGlenFallsSection />
        <WhyStayNearSection />
        <OtherCabinsSection />
        <FAQSection />

        {/* Wrap-up */}
        <section className="bg-[#FAFAF8] py-16 px-6 lg:px-14">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-[#5C5850] font-jost text-sm leading-relaxed mb-4">
              Cabins near Glen Falls in Highlands, NC offer a rare combination of natural immersion and easy access to one of the area&apos;s most beloved waterfalls. Whether you visit the falls once or return every morning of your stay, proximity to Glen Falls adds a dimension to a Highlands trip that&apos;s hard to replicate from a more central location.
            </p>
            <p className="text-[#5C5850] font-jost text-sm leading-relaxed">
              For more on planning a trip to the area, explore our guides to{' '}
              <Link to="/things-to-do" className="text-[#2D4A3E] underline underline-offset-2 hover:text-[#1e3329] transition-colors cursor-pointer">
                things to do in Highlands NC
              </Link>
              {' '}and{' '}
              <Link to="/pet-friendly-cabins-highlands-nc" className="text-[#2D4A3E] underline underline-offset-2 hover:text-[#1e3329] transition-colors cursor-pointer">
                pet friendly cabins in Highlands NC
              </Link>
              .
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#2D4A3E] py-20 px-6 lg:px-14">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-[#C9A96E] text-xs tracking-[0.35em] uppercase font-jost mb-5">/ Book Your Stay</p>
            <h2 className="font-cormorant text-5xl md:text-6xl text-white font-light mb-5">
              Stay Near<br /><em className="italic">Glen Falls</em>
            </h2>
            <p className="text-white/65 font-jost text-sm leading-relaxed mb-10 max-w-lg mx-auto">
              Glen Falls Luxury Cabin in Highlands, NC is the top-rated option for visitors who want to stay close to the falls. Check availability and book your stay directly or through Airbnb.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://www.airbnb.com/rooms/900012473285450950"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#C9A96E] hover:bg-[#B8954F] text-white text-xs tracking-[0.2em] uppercase font-jost font-medium px-10 py-4 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                View Glen Falls Cabin on Airbnb
              </a>
              <Link
                to="/best-luxury-cabins"
                className="border border-white/40 text-white text-xs tracking-[0.2em] uppercase font-jost font-medium px-10 py-4 rounded-full hover:bg-white/10 transition-all cursor-pointer whitespace-nowrap"
              >
                Browse All Cabins
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
